import React from 'react';
import { faScissors, faList, faHome, faPuzzlePiece, faTag } from '@fortawesome/pro-regular-svg-icons';
import { faVanShuttle, faWhistle, faDogLeashed } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { MembershipItem, MEMBERSHIP_ITEM_TYPES, MembershipItemTypeValue } from '@spike/membership-model';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Dropdown } from 'components/UI/V2/Dropdown/Dropdown';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ItemSelectionDrawer, ItemDisplayConfig } from 'components/UI/V3/ItemSelectionDrawer/ItemSelectionDrawer';
import clsx from 'clsx';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import Service from 'model/Service';
import { MembershipDiscountedItemsTableItem } from './MembershipDiscountedItemsTableItem';
import {
    useMembershipDiscountedItemDrawers,
    DiscountedDrawerState,
    DiscountMap
} from '../hooks/useMembershipDiscountedItemDrawers';
import { useMembershipItemDisplayConfigs } from '../hooks/useMembershipItemDisplayConfigs';
import { DiscountField, DiscountType } from './DiscountField';

function renderDiscountedItemDrawer<T extends { id?: string | number; name: string }>(
    key: string,
    title: string,
    selectAllLabel: string,
    searchPlaceholder: string,
    state: DiscountedDrawerState<T>,
    itemDisplay: ItemDisplayConfig<T>
): React.ReactElement {
    const renderItemAction = (item: T, isChecked: boolean) => {
        if (!isChecked) return null;
        const itemId = String(item.id);
        const discount = state.drawer.tempDiscounts[itemId] || { value: 0, type: 'percentage' as DiscountType };
        const maxAmount = itemDisplay.getNumericPrice ? itemDisplay.getNumericPrice(item) : undefined;
        return (
            <DiscountField
                value={discount.value}
                discountType={discount.type}
                maxAmount={maxAmount}
                onChange={(value, type) => state.drawer.setTempDiscount(itemId, value, type)}
            />
        );
    };

    return (
        <ItemSelectionDrawer<T>
            key={key}
            isOpen={state.drawer.isOpen}
            title={title}
            items={state.availableItems}
            selectedItems={state.currentItems}
            tempSelectedItems={state.drawer.tempSelectedItems}
            searchQuery={state.drawer.searchQuery}
            filteredItems={state.drawer.filteredItems}
            selectAllLabel={selectAllLabel}
            searchPlaceholder={searchPlaceholder}
            itemDisplay={itemDisplay}
            renderItemAction={renderItemAction}
            onClose={state.drawer.closeDrawer}
            onSearchChange={state.drawer.setSearchQuery}
            onTempSelectionChange={state.drawer.setTempSelectedItems}
            onApply={state.drawer.handleApply}
            onCancel={state.drawer.handleCancel}
        />
    );
}

interface MembershipDiscountedItemsTableProps {
    items: MembershipItem[];
    onChange: (items: MembershipItem[]) => void;
    excludedItemIDs: string[];
    marketplaceID: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            marginTop: 30,

            [theme.breakpoints.down('sm')]: {
                marginTop: 30
            }
        },
        title: {
            fontSize: 15,
            lineHeight: 1.2,
            fontWeight: 500,
            color: '#000000',
            marginBottom: 8
        },
        description: {
            fontSize: 15,
            lineHeight: 1.4,
            fontWeight: 400,
            color: '#7A7A7A',
            marginBottom: 24
        },
        grid: {
            display: 'grid',
            gap: '16px 10px',
            gridTemplateColumns: 'minmax(90px, 1fr) 155px',

            [theme.breakpoints.up('md')]: {
                gap: 16,
                gridTemplateColumns: '1fr 100px 160px 90px'
            }
        },
        header: {
            paddingBottom: 16,
            borderBottom: 'solid 2px #000000',

            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        headerLabel: {
            'fontSize': 15,
            'lineHeight': 1.6,
            'fontWeight': 600,
            'color': '#000000',
            'textAlign': 'center',

            '&:first-child': {
                textAlign: 'left'
            },
            '&:last-child': {
                textAlign: 'right'
            }
        },
        dropdown: {
            display: 'block',
            marginTop: 45
        },
        headerWithTooltip: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6
        },
        tooltipIcon: {
            fontSize: 14,
            color: '#5e8677',
            cursor: 'pointer'
        },
        tooltip: {
            textAlign: 'center'
        }
    })
);

export const MembershipDiscountedItemsTable: React.FC<MembershipDiscountedItemsTableProps> = props => {
    const classes = useStyles();

    const {
        openDrawerForType,
        resolveAndOpenDrawer,
        grooming,
        daycare,
        boarding,
        walking,
        transportation,
        training,
        addons,
        products
    } = useMembershipDiscountedItemDrawers({
        items: props.items,
        excludedItemIDs: props.excludedItemIDs,
        marketplaceID: props.marketplaceID,
        onChange: props.onChange
    });

    const { serviceDisplay, addonDisplay, productDisplay } = useMembershipItemDisplayConfigs();

    const deleteDiscountedItemHandler = (uuid: string) => {
        props.onChange(props.items.filter(i => i.uuid !== uuid));
    };

    const dropdownOptions = React.useMemo(() => {
        const options = [];

        if (grooming.catalogItems.length > 0) {
            options.push({
                icon: faScissors,
                label: 'Grooming Service',
                value: MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE
            });
        }
        if (daycare.catalogItems.length > 0) {
            options.push({ icon: faList, label: 'Daycare Service', value: MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE });
        }
        if (boarding.catalogItems.length > 0) {
            options.push({ icon: faHome, label: 'Boarding Service', value: MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE });
        }
        if (walking.catalogItems.length > 0) {
            options.push({
                icon: faDogLeashed,
                label: 'Walking Service',
                value: MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE
            });
        }
        if (transportation.catalogItems.length > 0) {
            options.push({
                icon: faVanShuttle,
                label: 'Transportation Service',
                value: MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE
            });
        }
        if (training.catalogItems.length > 0) {
            options.push({ icon: faWhistle, label: 'Training Service', value: MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE });
        }
        if (addons.catalogItems.length > 0) {
            options.push({ icon: faPuzzlePiece, label: 'Add-On', value: MEMBERSHIP_ITEM_TYPES.ADD_ON });
        }
        if (products.catalogItems.length > 0) {
            options.push({ icon: faTag, label: 'Product', value: MEMBERSHIP_ITEM_TYPES.PRODUCT });
        }

        return options;
    }, [
        grooming.catalogItems,
        daycare.catalogItems,
        boarding.catalogItems,
        walking.catalogItems,
        transportation.catalogItems,
        training.catalogItems,
        addons.catalogItems,
        products.catalogItems
    ]);

    return (
        <>
            <div className={classes.container}>
                <Typography className={classes.title}>Discounted Items</Typography>
                <Typography className={classes.description}>
                    Choose which services, add-ons, and products will include discounts for members.
                </Typography>

                <div className={clsx(classes.grid, classes.header)}>
                    <div className={classes.headerLabel}>Item</div>
                    <div className={classes.headerLabel}>Qty</div>
                    <div className={classes.headerLabel}>
                        <div className={classes.headerWithTooltip}>
                            Discount
                            <Tooltip
                                content="Discount is an amount; taxes are computed on the discounted price when applicable."
                                placement="bottom"
                                className={classes.tooltip}
                            >
                                <div>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className={classes.tooltipIcon}
                                    />
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div />
                </div>

                <div>
                    {props.items.map(item => (
                        <MembershipDiscountedItemsTableItem
                            key={item.uuid}
                            item={item}
                            className={classes.grid}
                            onChange={updatedItem =>
                                props.onChange(props.items.map(i => (i.uuid === updatedItem.uuid ? updatedItem : i)))
                            }
                            onDelete={deleteDiscountedItemHandler}
                            onEdit={() => resolveAndOpenDrawer(item)}
                            currentItems={props.items}
                            excludedItemIDs={props.excludedItemIDs}
                        />
                    ))}
                </div>

                <Dropdown
                    label="Add Item"
                    options={dropdownOptions}
                    className={classes.dropdown}
                    onSelect={openDrawerForType}
                />
            </div>

            {renderDiscountedItemDrawer<Service>(
                'grooming',
                'Select Grooming Services',
                'All Services',
                'Search by service name',
                grooming,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<Service>(
                'daycare',
                'Select Daycare Services',
                'All Services',
                'Search by service name',
                daycare,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<Service>(
                'boarding',
                'Select Boarding Services',
                'All Services',
                'Search by service name',
                boarding,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<Service>(
                'walking',
                'Select Walking Services',
                'All Services',
                'Search by service name',
                walking,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<Service>(
                'transportation',
                'Select Transportation Services',
                'All Services',
                'Search by service name',
                transportation,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<Service>(
                'training',
                'Select Training Services',
                'All Services',
                'Search by service name',
                training,
                serviceDisplay
            )}
            {renderDiscountedItemDrawer<AddOnService>(
                'addons',
                'Select Add-ons',
                'All Add-ons',
                'Search by add-on name',
                addons,
                addonDisplay
            )}
            {renderDiscountedItemDrawer<Product>(
                'products',
                'Select Products',
                'All Products',
                'Search by product name',
                products,
                productDisplay
            )}
        </>
    );
};
